/**
 * A non-blocking, promise-based confirmation.
 *
 * `window.confirm` is synchronous: it freezes the whole renderer until the person answers, which
 * is why driving a confirmed action (Rebuild) over automation looked like a hang and why the busy
 * spinner behind it could not animate. This replaces it with an in-app modal that resolves a
 * promise, so a handler keeps its `if (!(await confirm(...))) return;` shape while the main thread
 * stays live. Enter confirms, Escape or a backdrop click cancels, and focus lands on the confirm
 * button so the keyboard flow matches the native dialog it stands in for.
 */

import * as React from 'react';
import { createPortal } from 'react-dom';

export interface ConfirmOptions {
  message: string;
  title?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** `danger` tints the confirm button for an irreversible action (approve, clear). */
  tone?: 'default' | 'danger';
}

function Dialog({ opts, onConfirm, onCancel }: { opts: ConfirmOptions; onConfirm: () => void; onCancel: () => void }) {
  const confirmRef = React.useRef<HTMLButtonElement | null>(null);
  React.useEffect(() => { confirmRef.current?.focus(); }, []);
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
      else if (e.key === 'Enter') { e.preventDefault(); onConfirm(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onConfirm, onCancel]);

  return createPortal(
    <div
      className="confirm-backdrop"
      role="presentation"
      // Only a click on the backdrop itself cancels — not one that bubbles up from inside the card.
      onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div className="confirm-card" role="alertdialog" aria-modal="true"
        aria-label={opts.title ?? 'Confirm'}>
        {opts.title && <h2 className="confirm-title">{opts.title}</h2>}
        <p className="confirm-message">{opts.message}</p>
        <div className="confirm-actions">
          <button type="button" className="button" onClick={onCancel}>{opts.cancelLabel ?? 'Cancel'}</button>
          <button type="button" ref={confirmRef}
            className={`button ${opts.tone === 'danger' ? 'danger' : 'primary'}`}
            onClick={onConfirm}>{opts.confirmLabel ?? 'Confirm'}</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * Returns `confirm(opts)` — a promise that resolves true/false — and `confirmDialog`, the element
 * to render once anywhere in the component. The resolver lives in a ref so settling it is a plain
 * side effect, not a state update that a StrictMode double-invoke could run twice.
 */
export function useConfirm() {
  const [opts, setOpts] = React.useState<ConfirmOptions | null>(null);
  const resolverRef = React.useRef<((ok: boolean) => void) | null>(null);

  const confirm = React.useCallback((o: ConfirmOptions | string) => {
    const options = typeof o === 'string' ? { message: o } : o;
    return new Promise<boolean>((resolve) => {
      // A second confirm() before the first is answered cancels the first — one modal at a time.
      resolverRef.current?.(false);
      resolverRef.current = resolve;
      setOpts(options);
    });
  }, []);

  const settle = React.useCallback((ok: boolean) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setOpts(null);
    resolve?.(ok);
  }, []);

  const confirmDialog = opts
    ? <Dialog opts={opts} onConfirm={() => settle(true)} onCancel={() => settle(false)} />
    : null;

  return { confirm, confirmDialog };
}
